import React, { useCallback, useEffect, useState, useRef } from "react";
import { Container, Section } from "components/shared";
import cx from "classnames";
import { Button } from "components/components/atoms";
import styled from "styled-components";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import { parsePrice } from "utils/parsePrice";
import { useCountryValue } from "stores/country";
import useNewColorSchemaOptions from "utils/hooks/useNewColorSchemaOptions";
import { scrollToSection } from "utils/scrollTo";
import { useLocation } from "react-router-dom";
import useFavorites from "utils/hooks/useFavorites";

import loadable from "@loadable/component";
const ContentBlock = loadable(() =>
  import("components/components/molecules/ContentBlock")
);
const QMICard = loadable(() =>
  import("components/components/molecules/QMICard")
);
const QMICardMobile = loadable(() =>
  import("components/components/molecules/QMICardMobile")
);

const StyledSection = styled(Section)`
  padding-bottom: 1.25rem !important;
`;

const StyledContainer = styled(Container)`
  min-height: 17rem;
  @media ${breakpoints.md} {
    min-height: 31.25rem;
  }
`;

const getSlides = (
  cards,
  onFocusChange,
  country,
  newSchemaColorHexaCode = "",
  newSchemaTextColorHexaCode = "",
  showNewSchemaColor = false,
  desktopVisibleCards,
  priceDifferentialText,
  favoritesList,
  handleFavoriteClick,
  itemRefShowMore
) => {
  return cards
    .sort((a, b) => {
      const aPrice =
        a.price.price === ""
          ? null
          : a.price.price === "$0"
          ? null
          : parsePrice(a.price.price);
      const bPrice =
        b.price.price === ""
          ? null
          : b.price.price === "$0"
          ? null
          : parsePrice(b.price.price);

      if (aPrice !== null && bPrice !== null) {
        return aPrice - bPrice;
      }

      if (aPrice === null)
        return bPrice === null
          ? a.title.toLowerCase().localeCompare(b.title.toLowerCase())
          : 1;
      return -1;
    })
    .reduce((accum, c, i) => {
      accum.push(
        <div
          tabIndex={-1}
          ref={(e) => (itemRefShowMore.current[i] = e)}
          key={c.id}
          className={
            "md:pb-16 md:pt-4 p-2 md:pr-0" +
            (i < desktopVisibleCards ? "" : " hidden")
          }
        >
          <span>&nbsp;</span>
          <QMICard
            data-aos="fade-left"
            data-aos-delay={i * 150}
            className="bg-light-blue w-full rounded"
            card={c}
            country={country}
            newSchemaColorHexaCode={newSchemaColorHexaCode}
            newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
            showNewSchemaColor={showNewSchemaColor}
            priceDifferentialText={priceDifferentialText}
            favoritesList={favoritesList}
            handleFavoriteClick={handleFavoriteClick}
          />
        </div>
      );
      return accum;
    }, []);
};

const RecentlyViewed = ({ fields, rendering }) => {
  const { componentName } = rendering;
  const [{ country }] = useCountryValue();
  const {
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();

  const targetRefFontSize = useRef();
  const [dynamicClass, setDynamicClass] = useState("lg:grid-cols-2");
  const [position, setPosition] = useState(0);
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const isLandscape = ["xl"].includes(breakpoint);
  const isLandscapeXl = ["2xl"].includes(breakpoint);

  const [mobileVisibleCards, setMobileVisibleCards] = useState(4);

  const [desktopVisibleCards, setDesktopVisibleCards] = useState(
    isLandscapeXl ? 6 : isLandscape ? 4 : 2
  );

  const itemRefShowMore = useRef([]);
  const [lastDesktopVisibleCards, setLastDesktopVisibleCards] = useState([]);

  const priceDifferentialText =
    country === "USA"
      ? getFieldValue(fields, "priceDifferentialTextUSA")
      : getFieldValue(fields, "priceDifferentialTextCAN");

  const handleFavoriteClick = (id) => {
    const isFavorite = favoritesList.includes(id);
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  //code show more handle for show more buuton
  const result = getFieldValue(fields, "result") || {};

  const onFocusChange = useCallback(
    (index) => {
      if (index < slides.length - 2) {
        setPosition(index);
      }
    },
    [position]
  );

  //efc card-size
  useEffect(() => {
    // Focus  ...rest of state update
    const lastLen = lastDesktopVisibleCards[lastDesktopVisibleCards.length - 1];
    if (itemRefShowMore.current[lastLen]) {
      itemRefShowMore.current[lastLen].focus();
    }
  });

  useEffect(() => {
    const observer = new ResizeObserver(checkFontSizeChange);
    if (targetRefFontSize && targetRefFontSize.current) {
      observer.observe(targetRefFontSize.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  const checkFontSizeChange = () => {
    if (targetRefFontSize.current) {
      const fontSize = parseInt(
        getComputedStyle(targetRefFontSize.current).fontSize,
        10
      );
      if (fontSize > 20) {
        setDynamicClass("lg:grid-cols-1");
      } else {
        setDynamicClass("lg:grid-cols-2");
      }
    }
  };

  const [slides, setSlides] = useState(
    getSlides(
      result.cards || [],
      onFocusChange,
      country,
      newSchemaColorHexaCode,
      newSchemaTextColorHexaCode,
      showNewSchemaColor,
      desktopVisibleCards,
      priceDifferentialText,
      favoritesList,
      handleFavoriteClick,
      itemRefShowMore
    )
  );
  useEffect(() => {
    const newSlides = getSlides(
      result.cards || [],
      onFocusChange,
      country,
      newSchemaColorHexaCode,
      newSchemaTextColorHexaCode,
      showNewSchemaColor,
      desktopVisibleCards,
      priceDifferentialText,
      favoritesList,
      handleFavoriteClick,
      itemRefShowMore
    );
    setSlides(newSlides);
  }, [country, desktopVisibleCards, showNewSchemaColor, favoritesList]);

  const blueBackground = getFieldValue(fields, "blueBackground");

  const showContent = isMobile || position === 0;

  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (hash && hash.substring(1) === "availablehomes")
      setTimeout(() => {
        scrollToSection(hash.substring(1), 80);
      }, 2000);
  });

  if (slides.length < 1) return null;

  return (
    <StyledSection
      className={cx({
        "bg-light-blue": blueBackground,
        "bg-white": !blueBackground,
      })}
      id="availablehomes"
    >
      <StyledContainer
        className={cx("w-full h-full relative flex md:flex-col items-center")}
      >
        <div className="w-full h-full relative flex md:flex-row justify-between">
          <div className={cx("w-full md:w-5/12 lg:w-1/4 relative")}>
            <ContentBlock
              textColor={"blue"}
              heading={result.headline}
              copy={result.copy}
              ctas={[result.cta]}
              ButtonProps={{
                color: "mattamy-blue",
                className: "px-16",
                variant: isMobile ? "filled" : "outlined",
              }}
              cutOffTitle={false}
              newSchemaColorHexaCode={newSchemaColorHexaCode}
              newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
              showNewSchemaColor={showNewSchemaColor}
              isMobile={isMobile}
            />
          </div>

          {!isMobile && (
            <div
              className={cx(
                "ml-16 relative w-full h-full flex flex-col space-between -mt-4"
              )}
            >
              <div
                ref={targetRefFontSize}
                className={`w-full mt-8 md:mt-0 grid grid-cols-1 ${dynamicClass} 2xl:grid-cols-3 gap-4`}
              >
                {slides}
              </div>
              <div className="w-full mt-0 text-center">
                <Button
                  className={cx("mt-4", {
                    //"focus:bg-mattamy-blue-dark focus:text-white": !showNewSchemaColor,
                    hidden: !(result.cards.length > desktopVisibleCards),
                  })}
                  showNewSchemaColor={showNewSchemaColor}
                  color={
                    showNewSchemaColor ? newSchemaColorHexaCode : "mattamy-blue"
                  }
                  textColor={
                    showNewSchemaColor ? newSchemaTextColorHexaCode : "white"
                  }
                  variant="filled"
                  buttonDescription={"SHOW MORE"}
                  onClick={() => {
                    if (result.cards.length > desktopVisibleCards)
                      setDesktopVisibleCards(
                        desktopVisibleCards +
                          (isLandscapeXl ? 3 : isLandscape ? 2 : 1)
                      );

                    setLastDesktopVisibleCards((prvs) => [
                      ...prvs,
                      desktopVisibleCards,
                    ]);
                  }}
                >
                  <p className="w-40">{"SHOW MORE"}</p>
                </Button>
              </div>
            </div>
          )}
        </div>
      </StyledContainer>

      {isMobile && (
        <div
          ref={targetRefFontSize}
          className={cx(
            "relative w-full h-full transition-all duration-200 flex space-between -mt-4"
          )}
        >
          <div className="w-full md:mt-0 mx-4">
            {result.cards.map((sc, index) => (
              <QMICardMobile
                key={sc.idSaleCenter}
                {...sc}
                country={country}
                index={index}
                mobileVisibleCards={mobileVisibleCards}
                newSchemaColorHexaCode={newSchemaColorHexaCode}
                newSchemaTextColorHexaCode={"#000"}
                showNewSchemaColor={showNewSchemaColor}
                priceDifferentialText={priceDifferentialText}
                favoritesList={favoritesList}
              />
            ))}

            <Button
              className={cx("mt-4", {
                //"focus:bg-mattamy-blue-dark focus:text-white": !showNewSchemaColor,
                hidden: !(result.cards.length > mobileVisibleCards),
              })}
              showNewSchemaColor={showNewSchemaColor}
              color={
                showNewSchemaColor ? newSchemaColorHexaCode : "mattamy-blue"
              }
              textColor={
                showNewSchemaColor ? newSchemaTextColorHexaCode : "white"
              }
              variant="filled"
              buttonDescription={"SHOW MORE"}
              onClick={() => {
                if (result.cards.length > mobileVisibleCards)
                  setMobileVisibleCards(mobileVisibleCards + 4);
              }}
            >
              <p className="w-40">{"SHOW MORE"}</p>
            </Button>
          </div>
        </div>
      )}
    </StyledSection>
  );
};

export default RecentlyViewed;
